import React, { useState } from 'react';
import { Body, Button, ReadOnlyField, TinyTitle, palettes, Input, useFlashMessage } from '@indigo-ag/component-module';
import { AgGroup } from '@indigo-ag/schema';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { useMutation } from '@apollo/client';
import i18next from 'core/i18n';
import { useHistory } from 'react-router-dom';

import { Card, text, date, currency, createStylesHook, utils } from 'shared';
import { upsertCessionGroupId } from 'features/brazil-grower/queries';

const useStyles = createStylesHook({
  recordBox: {
    marginBottom: 30,
    '& p': {
      marginTop: 2
    },
    '& h6': {
      color: palettes.neutral['600']
    }
  }
});

interface CessionGroupInformationProps {
  cessionGroup: AgGroup;
}

export const CessionGroupInformation: React.FunctionComponent<CessionGroupInformationProps> = ({ cessionGroup }) => {
  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * state
   */
  const [isEditingCessionGroupId, setIsEditingCessionGroupId] = useState(false);
  const [cessionGroupId, setCessionGroupId] = useState(cessionGroup.id);

  /**
   * Mutations
   */
  const [upsertCessionGroupIdFn, { loading }] = useMutation(upsertCessionGroupId);

  /**
   * History
   */
  const history = useHistory();

  const isValidCessionGroupId = (cessionNumber: number) =>
    !(!cessionNumber || isNaN(Number(cessionNumber))) && cessionNumber !== cessionGroup?.id;

  const { addFlashMessage } = useFlashMessage();

  const handleEditCessionGroupId = () =>
    upsertCessionGroupIdFn({
      variables: {
        currentNumber: cessionGroup?.id,
        newNumber: cessionGroupId
      }
    })
      .then(() => {
        history.push(`/cessions/${cessionGroupId}`);

        addFlashMessage({
          content: utils.text.firstLetterUppercase(i18next.t('cessionNumberUpdated')),
          title: '',
          variant: 'success'
        });
      })
      .catch(e => {
        setCessionGroupId(cessionGroup.id);

        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => setIsEditingCessionGroupId(false));

  const handleCancelEditCessionGroupId = () => {
    setIsEditingCessionGroupId(false);
    setCessionGroupId(cessionGroup.id);
  };

  return (
    <Card title={i18next.t('cessionInformation')}>
      {isEditingCessionGroupId ? (
        <div className={classes.recordBox}>
          <Input
            label={utils.text.toTitleCase(i18next.t('cessionNumber'))}
            onChange={e => setCessionGroupId(Number(e.target.value))}
            value={!cessionGroupId || isNaN(cessionGroupId) ? '' : String(cessionGroupId)}
            sizeVariant="slim"
          />
          <div className="flex align-items-center flex-space-btw">
            <Button onClick={handleCancelEditCessionGroupId} variant="secondary" size="small">
              {i18next.t('cancel')}
            </Button>
            <Button
              onClick={() => handleEditCessionGroupId()}
              size="small"
              busy={loading}
              disabled={!isValidCessionGroupId(cessionGroupId)}
            >
              {i18next.t('save')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={classes.recordBox}>
          <TinyTitle>{text.toTitleCase(i18next.t('cessionNumber'))}</TinyTitle>
          <div className="flex flex-space-btw">
            <Body letMeMargin style={{ paddingTop: '7px' }}>
              {cessionGroupId.toString()}
            </Body>
            <Button
              faIcon={faPencil}
              variant="tertiary"
              onClick={() => setIsEditingCessionGroupId(true)}
              size="small"
              faIconColor={palettes.brand.gray[600]}
            />
          </div>
        </div>
      )}
      <ReadOnlyField
        label={text.toTitleCase(i18next.t('date'))}
        value={date.formatDate(cessionGroup.cessionDate || new Date())}
      />
      <ReadOnlyField
        label={text.toTitleCase(i18next.t('totalContractValue'))}
        value={cessionGroup.totalContractValue ? currency.formatBrazilCurrency(cessionGroup.totalContractValue) : '-'}
      />
    </Card>
  );
};
